import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Button from '../../layouts/theme-onboarding/Button';
import Typography from '@mui/material/Typography';
import { ThemeProvider } from '@mui/material/styles';
import Information from '../../components/signup/Information';
import OcrAndEkyc from './../../components/signup/OcrAndEkyc';
import Product from '../../components/signup/Product';
import Confirm from '../../components/signup/Confirm';
import Address from './../../components/signup/Address';
import SignContract from './../../components/signup/SignContract';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import Theme from '../../layouts/theme-onboarding/Theme';
import { useMediaQuery } from '@mui/material';
import { initStep0Data, initStep1Data, initStep2Data, initStep3Data, initStep4Data, initStep5Data } from '../../utils/object/Signup';
import { useDispatch, useSelector } from 'react-redux';
import { showAlert } from '../../redux/create-actions/AlertAction';
import { showOtpModal } from '../../redux/create-actions/OtpAction';
import { backToPreviousStep, goToNextStep } from '../../redux/create-actions/OnboardingAction';
import { useNavigate } from 'react-router-dom';
import { openLoadingApi, closeLoadingApi } from '../../redux/create-actions/LoadingAction';
import Header from '../../components/signup/Header';
import Color from '../../layouts/Color';
import { graphCheckNewClient, graphCreateContract, graphUpdateAdditionalInformation, graphUpdateEkycFlag } from '../../services/Onboarding';
import Storage from '../../services/Storage';

const steps = ['Information', 'OCR & eKYC', 'Product', 'Confirm', 'Address', 'Contract'];
const miniSteps1 = ['Information', 'OCR & eKYC', 'Product'];
const miniSteps2 = ['Confirm', 'Address', 'Contract'];

const stepStyle = { pt: 3, pb: 5, overflow: 'hidden' };
const root = { backgroundImage: `url(${process.env.PUBLIC_URL + '/assets/images/signup/background.png'})`, minHeight: '100vh' };
const boxGroupButton = { display: 'flex', justifyContent: 'flex-end' };
const buttonSubmit = {
  mt: 3, ml: 1,
  color: Color.Button,
  backgroundColor: Color.Main,
  '&:hover': { background: Color.Button, color: Color.Normal }
};
const buttonBack = { mt: 3, ml: 1 };
const container = {
  mt: 6,
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
};
const paperStyle = { my: { xs: 3, md: 6 }, p: { xs: 2, md: 3 }, mt: 10 };
const steperLabel = {
  '& .MuiStepIcon-root.Mui-active': { color: Color.Main },
  '& .MuiStepLabel-label.Mui-active': { color: Color.Main, fontWeight: 700 },
  '& .Mui-completed': { color: Color.Active, fontWeight: 700 },
};

export default function Signup() {
  const matchDownMD = useMediaQuery(Theme.breakpoints.down('md'));
  const matchDownSM = useMediaQuery(Theme.breakpoints.down('sm'));
  const [activeStep, setActiveStep] = useState(0);
  const [step0, setStep0] = useState(initStep0Data('', '', '', ''));
  const [step1, setStep1] = useState(initStep1Data(null, null, null));
  const [step2, setStep2] = useState(initStep2Data(null, null, null));
  const [step3, setStep3] = useState(initStep3Data('', '', '', '', '', '', '', ''));
  const [step4, setStep4] = useState(initStep4Data('', '', ''));
  const [step5, setStep5] = useState(initStep5Data());
  const [step0Result, setStep0Result] = useState({});
  const [step4Result, setStep4Result] = useState(null);
  const [, setStep5Result] = useState({});
  const [renderEkyc, setRenderEkyc] = useState(true);
  const [step1RollBack, setStep1RollBack] = useState(initStep1Data(null, null, null));
  const [contractPresignedUrl, setContractPresignedUrl] = useState(null);
  const [contractS3Key, setContractS3Key] = useState(null);
  const [contractId, setContractId] = useState(null);
  const data = useSelector(state => state.onboarding.data);
  const dispatch = useDispatch();
  let navigate = useNavigate();

  useEffect(() => {
    if (Storage.getLocalAccessToken() !== null) {
      navigate('/dashboard', { replace: true })
    }
  }, [navigate])

  useEffect(() => {
    if (activeStep === 0 && data.step === 1) {
      setActiveStep(activeStep + 1);
    }
    if (activeStep === 1 && data.step === 0) {
      setActiveStep(activeStep - 1);
      dispatch(backToPreviousStep());
    }
    if (data.onboardingData !== null && data.onboardingData.flag === 3 && activeStep === 1) {
      setRenderEkyc(false);
      let oldStep1 = {
        eKycFrontResponse: data.onboardingData.lastFrontEkyc,
        eKycBackResponse: data.onboardingData.lastBackEkyc,
        eKycFaceResponse: data.onboardingData.lastFaceEkyc,
      }
      setStep1(oldStep1);
      setStep1RollBack(oldStep1);
    }
    if (data.onboardingData !== null && data.onboardingData.flag === 5 && activeStep === 1) {
      setActiveStep(5);
      setContractPresignedUrl(data.onboardingData.contractPresignedUrl);
      setContractS3Key(data.onboardingData.contractS3Key);
      setContractId(data.onboardingData.contractId);
    }
    if (data.onboardingData !== null && data.onboardingData.flag === 6 && activeStep === 1) {
      //redirect signin page
      navigate("/signin", { replace: true });
      let alert = {
        anchorOrigin: {
          vertical: 'top',
          horizontal: 'center'
        },
        isOpen: true,
        message: "You have processed onboarding before. Please login to open other contract!",
        severity: 'success', title: 'Success'
      };
      dispatch(showAlert(alert));
    }
  }, [data.step, data.onboardingData, activeStep, dispatch, navigate]);

  const handleNext = () => {
    let alert = {
      anchorOrigin: {
        vertical: 'top',
        horizontal: 'center'
      },
      isOpen: true,
      severity: 'error',
      title: 'Error',
      message: '',
    };
    switch (activeStep) {
      case 0:
        if ((!step0.isPhoneError && !step0.isEmailError && !step0.isIdNumberError)
          && step0.email !== "" && step0.phone !== "" && step0.idNumber !== "") {
          dispatch(openLoadingApi());
          let promise = graphCheckNewClient(step0.phone, step0.email, step0.idType, step0.idNumber);
          promise.then(result => {
            setStep0Result(result);
            dispatch(closeLoadingApi());
            dispatch(showOtpModal(result.customerId, "ONBOARDING"));
          }).catch(function (error) {
            const err = JSON.parse(JSON.stringify(error))
            alert = { ...alert, message: err.response.errors[0].message };
            dispatch(closeLoadingApi());
            dispatch(showAlert(alert));
          });
        } else {
          alert = { ...alert, message: 'You need to input correct information!' };
          dispatch(showAlert(alert));
        }
        break;
      case 1:
        if (step1.eKycFrontResponse !== null && step1.eKycBackResponse !== null && step1.eKycFaceResponse !== null) {
          if (renderEkyc === true) {
            dispatch(openLoadingApi());
            let promise = graphUpdateEkycFlag(step0Result.customerId);
            promise.then(result => {
              dispatch(goToNextStep());
              dispatch(closeLoadingApi());
              setActiveStep(activeStep + 1);
            }).catch(function (error) {
              const err = JSON.parse(JSON.stringify(error))
              alert = { ...alert, message: err.response.errors[0].message };
              dispatch(closeLoadingApi());
              dispatch(showAlert(alert));
            });
          } else {
            dispatch(goToNextStep());
            setActiveStep(activeStep + 1);
          }
        } else {
          alert = { ...alert, message: 'You need to upload images and capture portrait!' };
          dispatch(showAlert(alert));
        }
        break;
      case 2:
        if (step2.productType !== null && step2.productCode !== "" && step2.productName !== null) {
          dispatch(goToNextStep());
          setActiveStep(activeStep + 1);
        } else {
          alert = { ...alert, message: 'You need to select product for onboarding contract!' };
          dispatch(showAlert(alert));
        }
        break;
      case 3:
        if (step2.productType === 1) {
          if ((!step3.isRefNameError && !step3.isRefPhoneError && !step3.isSalaryError && !step3.isCompanyError)
            && step3.occupation !== "" && step3.position !== "" && step3.salary !== "" && step3.company !== "") {
            dispatch(goToNextStep());
            setActiveStep(activeStep + 1);
          } else {
            alert = { ...alert, message: 'You need to input correct information!' };
            dispatch(showAlert(alert));
          }
        } else if (step2.productType === 2) {
          if ((!step3.isRefNameError && !step3.isRefPhoneError && !step3.isSalaryError && !step3.isCompanyError)
            && step3.occupation !== "" && step3.position !== "" && step3.salary !== "" && step3.company !== "" &&
            step3.refName !== "" && step3.refPhone !== "" && step3.relationship !== "") {
            dispatch(goToNextStep());
            setActiveStep(activeStep + 1);
          } else {
            alert = { ...alert, message: 'You need to input correct information!' };
            dispatch(showAlert(alert));
          }
        }
        break;
      case 4:
        let promise;
        if (step2.productType === 1) {
          dispatch(openLoadingApi());
          promise = graphUpdateAdditionalInformation(step0Result.customerId, step0.idType, step0.idNumber, step1.eKycFrontResponse.name,
            step1.eKycFrontResponse.sex, step1.eKycFrontResponse.dob, step1.eKycFrontResponse.nationality, step3.company,
            step1.eKycFrontResponse.address, step4.permanentAddress, step4.cardAddress, step0.email, step3.occupation, step3.position, step3.salary,
            null, null, null, null, step2.productCode, step0.phone, step2.productType, step1.eKycBackResponse.issue_date, step1.eKycBackResponse.issue_loc);
          promise.then(result => {
            setStep4Result(result);
            dispatch(closeLoadingApi());
            dispatch(goToNextStep());
            setActiveStep(activeStep + 1);
          }).catch(function (error) {
            const err = JSON.parse(JSON.stringify(error))
            alert = { ...alert, message: err.response.errors[0].message };
            dispatch(closeLoadingApi());
            dispatch(showAlert(alert));
          });
        } else if (step2.productType === 2) {
          dispatch(openLoadingApi());
          promise = graphUpdateAdditionalInformation(step0Result.customerId, step0.idType, step0.idNumber, step1.eKycFrontResponse.name,
            step1.eKycFrontResponse.sex, step1.eKycFrontResponse.dob, step1.eKycFrontResponse.nationality, step3.company,
            step1.eKycFrontResponse.address, step4.permanentAddress, step4.cardAddress, step0.email, step3.occupation, step3.position, step3.salary,
            step3.refName, step3.refPhone, step3.relationship, step3.refNote, step2.productCode, step0.phone, step2.productType, step1.eKycBackResponse.issue_date, step1.eKycBackResponse.issue_loc);
          promise.then(result => {
            setStep4Result(result);
            dispatch(closeLoadingApi());
            dispatch(goToNextStep());
            setActiveStep(activeStep + 1);
          }).catch(function (error) {
            const err = JSON.parse(JSON.stringify(error))
            alert = { ...alert, message: err.response.errors[0].message };
            dispatch(closeLoadingApi());
            dispatch(showAlert(alert));
          });
        }
        break;
      case 5:
        if (step5.isAgree === false) {
          alert = { ...alert, message: "Please agree with Apollo terms before submiting contract!" };
          dispatch(showAlert(alert));
        } else {
          let contractIdValue = step4Result !== null ? step4Result.contractId : contractId;
          dispatch(openLoadingApi());
          let promise = graphCreateContract(contractIdValue);
          promise.then(result => {
            setStep5Result(result);
            alert = { ...alert, severity: "success", title: "Success", message: "Open contract successful!" };
            dispatch(closeLoadingApi());
            dispatch(goToNextStep());
            setActiveStep(activeStep + 1);
            dispatch(showAlert(alert));
          }).catch(function (error) {
            const err = JSON.parse(JSON.stringify(error))
            alert = { ...alert, message: err.response.errors[0].message };
            dispatch(closeLoadingApi());
            dispatch(showAlert(alert));
          });
        }
        break;
      default:
        break;
    }
  };

  const handleBack = () => {
    setActiveStep(activeStep - 1);
    dispatch(backToPreviousStep());
  };

  const getBaseInformation = (data) => {
    setStep0(data);
  }

  const getEkycResponse = (data) => {
    setStep1(data);
  }

  const getSelectedProduct = (data) => {
    setStep2(data);
  }

  const getAdditionalInformation = (data) => {
    setStep3(data);
  }

  const getAddressInformation = (data) => {
    setStep4(data);
  }

  const getAgreeTerms = (data) => {
    setStep5(data);
  }

  function getStepContent(step) {
    switch (step) {
      case 0:
        return <Information onUpdateStep0Data={getBaseInformation} />;
      case 1:
        return <OcrAndEkyc customerId={step0Result.customerId} onUpdateStep1Data={getEkycResponse} idNumber={step0.idNumber} renderEkyc={renderEkyc} step1RollBack={step1RollBack} />;
      case 2:
        return <Product onUpdateStep2Data={getSelectedProduct} />;
      case 3:
        return <Confirm step0Data={step0} step1Data={step1} step2Data={step2} onUpdateStep3Data={getAdditionalInformation} productType={step2.productType} />;
      case 4:
        return <Address
          baseAddress={step1.eKycFrontResponse.address}
          onUpdateStep4Data={getAddressInformation}
        />;
      case 5:
        return <SignContract onUpdateStep5Data={getAgreeTerms} step4Response={step4Result} contractPresignedUrl={contractPresignedUrl} contractS3Key={contractS3Key} />;
      default:
        throw new Error('Unknown step');
    }
  }

  const renderActiveStep = (active) => {
    if (matchDownSM && active < 3) {
      return (<Stepper activeStep={active} alternativeLabel sx={stepStyle}>
        {miniSteps1.map((label, index) => (
          <Step key={label} index={index}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>);
    } else if (matchDownSM && active >= 3) {
      return (<Stepper activeStep={active} alternativeLabel sx={stepStyle}>
        {miniSteps2.map((label, index) => (
          <Step key={label} index={index + 3}>
            <StepLabel >{label}</StepLabel>
          </Step>
        ))}
      </Stepper>);
    } else {
      return (<Stepper alternativeLabel activeStep={active} sx={stepStyle}>
        {steps.map((label,) => (
          <Step key={label}>
            <StepLabel sx={steperLabel}>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>);
    }
  }

  return (
    <div style={root}>
      <Header></Header>
      <ThemeProvider theme={Theme}>
        <Container component="main" maxWidth="lg" sx={container}>
          <Typography variant="h4">Onboarding</Typography>
          <Paper variant="elevation" sx={paperStyle}>
            <Typography variant={'h3'} marked="left" gutterBottom color={Color.Main}>Customer Onboarding</Typography>
            {renderActiveStep(activeStep)}
            <React.Fragment>
              {activeStep === steps.length ? (
                <React.Fragment>
                  <Typography
                    variant={matchDownMD ? 'h5' : 'h6'}
                    marked="left" gutterBottom>Thank you for your registration.</Typography>
                  <Typography
                    variant={matchDownMD ? 'body2' : 'body1'}
                  >
                    We have emailed your contract information and will send you the terms during using
                    our services.
                  </Typography>
                  <Typography
                    variant={matchDownMD ? 'body2' : 'body1'}
                    sx={{color: '#FF0000'}}
                  >
                    In non-production version, default password is "OpenWayVN"
                  </Typography>
                </React.Fragment>
              ) : (
                <React.Fragment>
                  {getStepContent(activeStep)}
                  <Box sx={boxGroupButton}>
                    {activeStep !== 0 && (
                      <Button onClick={handleBack} sx={buttonBack}>
                        <ArrowBackIosIcon></ArrowBackIosIcon> Back
                      </Button>
                    )}
                    <Button
                      variant="contained"
                      onClick={handleNext}
                      sx={buttonSubmit}
                    >
                      {activeStep === steps.length - 1 ? 'Submit' : 'Next'}
                      <NavigateNextIcon></NavigateNextIcon>
                    </Button>
                  </Box>
                </React.Fragment>
              )}
            </React.Fragment>
          </Paper>
        </Container>
      </ThemeProvider>
    </div>
  );
}